import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

class ResponseError extends Error {
  response: Response;

  constructor(message: string, response: Response) {
    super(message);
    this.response = response;
  }
}

export interface RenewResponse {
  access_token: string;
  token_type: string;
  expires_in: string;
  scope: string;
}

export interface RevokeResponse {
  status: string;
}

export interface IssueResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

export interface IssuePayload {
  baseUrl: string;
  clientId: string;
  token: string;
  brand: string;
  tokenType?: string;
  scope?: string[];
  redirectUri?: string;
  nonce?: string;
  state?: string;
}

export const issueIAMToken = async ({
  baseUrl,
  brand,
  clientId: client_id,
  token,
  tokenType = 'JWT',
  scope = [],
  redirectUri: redirect_uri,
  nonce = uuidv4(),
  state = uuidv4()
}: IssuePayload): Promise<IssueResponse> => {
  const response = await fetch(`${baseUrl}/token/v1/${brand}/sm/issue`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      'X-Correlation-ID': uuidv4(),
      'X-Request-ID': uuidv4(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      token,
      tokenType,
      client_id,
      scope,
      redirect_uri,
      nonce,
      state
    })
  });

  if (!response.ok) {
    throw new ResponseError('Unable to issue token.', response);
  }

  return response.json();
};

export const validateToken = async ({
  baseUrl,
  token
}: {
  baseUrl: string;
  token: string;
}): Promise<void> => {
  return axios({
    method: 'GET',
    url: '/mortgages/v1/hello',
    baseURL: baseUrl,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

export const renewIAMToken = async ({
  baseUrl,
  brand,
  clientId,
  token
}: {
  baseUrl: string;
  clientId: string;
  token: string;
  brand: string;
}): Promise<RenewResponse> => {
  const response = await fetch(`${baseUrl}/token/v1/${brand}/sm/renew`, {
    method: 'PATCH',
    mode: 'cors',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Correlation-ID': uuidv4(),
      'X-Request-ID': uuidv4(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      access_token: token,
      client_id: clientId
    })
  });

  if (!response.ok) {
    throw new ResponseError('Unable to renew token.', response);
  }

  return response.json();
};

export const revokeIAMToken = async ({
  baseUrl,
  brand,
  clientId,
  token
}: {
  baseUrl: string;
  clientId: string;
  token: string;
  brand: string;
}): Promise<RevokeResponse> => {
  const response = await fetch(`${baseUrl}/token/v1/${brand}/sm/revoke`, {
    method: 'DELETE',
    mode: 'cors',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Correlation-ID': uuidv4(),
      'X-Request-ID': uuidv4(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      access_token: token,
      client_id: clientId
    })
  });

  if (!response.ok) {
    throw new ResponseError('Unable to revoke token.', response);
  }

  return response.json();
};
